import React, { useState, useMemo } from 'react';
import { Table, Badge, Card, Nav, Tab, Row, Col, Modal, Carousel, Button } from 'react-bootstrap';
import { FaHome, FaBed, FaBath, FaRuler, FaCalendar, FaDollarSign, FaBuilding, FaCamera } from 'react-icons/fa';
import { DealDetail } from '../../services/dealService';
import dealService from '../../services/dealService';

interface CompPhotoData {
  images: string[];
  address: string;
  bedrooms: number;
  bathrooms: number;
  livingArea: number;
  description: string;
  listingAgent: {
    name: string;
    officeName: string;
  };
  yearBuilt: number | null;
  propertyType: string;
  lotSize: number;
  daysOnMarket: number;
}

interface DealCompsReadOnlyProps {
  deal: DealDetail;
  compDetails: any[];
  compPhotos?: { [listingId: string]: CompPhotoData };
}

const DealCompsReadOnly: React.FC<DealCompsReadOnlyProps> = ({ deal, compDetails, compPhotos = {} }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [sortField, setSortField] = useState('comp_distance_miles');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [showModal, setShowModal] = useState(false);
  const [selectedComp, setSelectedComp] = useState<any>(null);
  const [selectedCompPhotos, setSelectedCompPhotos] = useState<CompPhotoData | null>(null);

  // Parse top comps from deal - handle both string and array formats
  const parseComps = (comps: any): any[] => {
    if (!comps) return [];
    if (typeof comps === 'string') {
      try {
        return JSON.parse(comps);
      } catch (e) {
        console.error('Failed to parse comps:', e);
        return [];
      }
    }
    return Array.isArray(comps) ? comps : [];
  };

  const topRenovatedComps = parseComps(deal.top_renovated_comps);
  const topAsIsComps = parseComps(deal.top_as_is_comps);

  // Combine all comps with type flag
  const allComps = [
    ...topRenovatedComps.map(c => ({ ...c, comp_type: 'renovated' })),
    ...topAsIsComps.map(c => ({ ...c, comp_type: 'as-is' }))
  ];

  // Filter comps based on tab
  const getFilteredComps = () => {
    if (activeTab === 'renovated') {
      return topRenovatedComps.map(c => ({ ...c, comp_type: 'renovated' }));
    } else if (activeTab === 'as-is') {
      return topAsIsComps.map(c => ({ ...c, comp_type: 'as-is' }));
    }
    return allComps;
  };

  // Sort comps
  const sortedComps = useMemo(() => {
    const comps = getFilteredComps();
    return [...comps].sort((a, b) => {
      const aVal = a[sortField] || 0;
      const bVal = b[sortField] || 0;
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [activeTab, sortField, sortOrder, topRenovatedComps, topAsIsComps]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const handleViewDetails = (comp: any) => {
    setSelectedComp(comp);
    // Get pre-fetched photo data if available
    if (comp.listing_id && compPhotos[comp.listing_id]) {
      setSelectedCompPhotos(compPhotos[comp.listing_id]);
    } else {
      setSelectedCompPhotos(null);
    }
    setShowModal(true);
  };

  const getClassificationBadge = (compType: string) => {
    const variants: Record<string, string> = {
      'renovated': 'success',
      'as-is': 'primary',
      'as_is': 'primary',
      'neutral': 'secondary'
    };
    
    const label = compType === 'as-is' ? 'As-Is' : 
                  compType === 'renovated' ? 'Renovated' : 
                  compType;
    
    return (
      <Badge bg={variants[compType] || 'secondary'}>
        {label}
      </Badge>
    );
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <FaHome className="me-2" />
            Comparable Properties Analysis
          </h5>
          <div className="text-muted">
            {sortedComps.length} comparable{sortedComps.length !== 1 ? 's' : ''} found
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <Tab.Container activeKey={activeTab}>
          <Nav variant="tabs" className="mb-3">
            <Nav.Item>
              <Nav.Link eventKey="all" onClick={() => setActiveTab('all')}>
                All Comps ({allComps.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="renovated" onClick={() => setActiveTab('renovated')}>
                Renovated ({topRenovatedComps.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="as-is" onClick={() => setActiveTab('as-is')}>
                As-Is ({topAsIsComps.length})
              </Nav.Link>
            </Nav.Item>
          </Nav>

          <div className="table-responsive">
            <Table hover className="align-middle">
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Address</th>
                  <th 
                    className="text-end cursor-pointer"
                    onClick={() => handleSort('close_price')}
                  >
                    Sold Price {sortField === 'close_price' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="text-center">$/Sq Ft</th>
                  <th className="text-center">Beds/Baths</th>
                  <th className="text-center">Sq Ft</th>
                  <th 
                    className="text-center cursor-pointer"
                    onClick={() => handleSort('comp_distance_miles')}
                  >
                    Distance {sortField === 'comp_distance_miles' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </th>
                  <th className="text-center">Close Date</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedComps.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-4 text-muted">
                      No comparable properties available for this filter
                    </td>
                  </tr>
                ) : (
                  sortedComps.map((comp, index) => (
                    <tr key={`${comp.listing_id}-${index}`}>
                      <td>{getClassificationBadge(comp.comp_type)}</td>
                      <td>{comp.address || 'N/A'}</td>
                      <td className="text-end">{formatPrice(comp.close_price || 0)}</td>
                      <td className="text-center">${comp.price_per_sqft?.toFixed(0) || 'N/A'}</td>
                      <td className="text-center">
                        {comp.bedrooms || 'N/A'}/{comp.bathrooms || 'N/A'}
                      </td>
                      <td className="text-center">
                        {comp.living_area ? comp.living_area.toLocaleString() : 'N/A'}
                      </td>
                      <td className="text-center">
                        {comp.distance_miles ? `${comp.distance_miles.toFixed(1)} mi` : 'N/A'}
                      </td>
                      <td className="text-center">{formatDate(comp.close_date)}</td>
                      <td className="text-center">
                        <Button 
                          variant="link" 
                          size="sm"
                          onClick={() => handleViewDetails(comp)}
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Tab.Container>

        {/* Comp Details Modal */}
        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              Comparable Property Details
              {selectedComp && (
                <small className="text-muted ms-2">
                  MLS #{selectedComp.listing_id || 'N/A'}
                </small>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedComp && (
              <div>
                {/* Photo Gallery */}
                {selectedCompPhotos && selectedCompPhotos.images.length > 0 ? (
                  <Carousel className="mb-4" interval={null}>
                    {selectedCompPhotos.images.map((image, index) => (
                      <Carousel.Item key={index}>
                        <img
                          className="d-block w-100"
                          src={image}
                          alt={`Property photo ${index + 1}`}
                          style={{ maxHeight: '400px', objectFit: 'contain' }}
                        />
                      </Carousel.Item>
                    ))}
                  </Carousel>
                ) : (
                  <div className="bg-light p-5 text-center mb-4">
                    <FaCamera size={48} className="text-muted mb-3" />
                    <p className="text-muted">No photos available for this property</p>
                  </div>
                )}

                {/* Property Details */}
                <Row>
                  <Col md={6}>
                    <h5>Property Information</h5>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted">Address:</td>
                          <td>{selectedCompPhotos?.address || selectedComp.address || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Type:</td>
                          <td>{selectedCompPhotos?.propertyType || 'Residential'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Year Built:</td>
                          <td>{selectedCompPhotos?.yearBuilt || selectedComp.year_built || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Living Area:</td>
                          <td>{selectedCompPhotos?.livingArea || selectedComp.living_area || 'N/A'} sq ft</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Lot Size:</td>
                          <td>{selectedCompPhotos?.lotSize ? `${selectedCompPhotos.lotSize.toLocaleString()} sq ft` : 'N/A'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                  <Col md={6}>
                    <h5>Sale Information</h5>
                    <Table borderless size="sm">
                      <tbody>
                        <tr>
                          <td className="text-muted">Sold Price:</td>
                          <td>{formatPrice(selectedComp.close_price || 0)}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Price/Sq Ft:</td>
                          <td>${selectedComp.price_per_sqft?.toFixed(0) || 'N/A'}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Close Date:</td>
                          <td>{formatDate(selectedComp.close_date)}</td>
                        </tr>
                        <tr>
                          <td className="text-muted">Days on Market:</td>
                          <td>{selectedCompPhotos?.daysOnMarket || selectedComp.days_on_market || 'N/A'}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </Col>
                </Row>

                {/* Property Description */}
                {selectedCompPhotos?.description && (
                  <div className="mt-3">
                    <h5>Property Description</h5>
                    <p className="text-muted">{selectedCompPhotos.description}</p>
                  </div>
                )}

                {/* Listing Agent Info */}
                {selectedCompPhotos?.listingAgent && (
                  <div className="mt-3">
                    <h5>Listing Information</h5>
                    <p className="text-muted mb-1">
                      <FaBuilding className="me-2" />
                      {selectedCompPhotos.listingAgent.name || 'N/A'}
                    </p>
                    <p className="text-muted">
                      {selectedCompPhotos.listingAgent.officeName || 'N/A'}
                    </p>
                  </div>
                )}
              </div>
            )}
          </Modal.Body>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default DealCompsReadOnly;